/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */

import { AppLanguage } from '../types';

let activeAudio: HTMLAudioElement | null = null;

// Pre-load voices on load so they are ready as soon as possible
if (typeof window !== 'undefined' && 'speechSynthesis' in window) {
  window.speechSynthesis.getVoices();
  if (window.speechSynthesis.onvoiceschanged !== undefined) {
    window.speechSynthesis.onvoiceschanged = () => {
      window.speechSynthesis.getVoices();
    };
  }
}

/**
 * Scores and selects the most appropriate Urdu-trained neural voice if available.
 */
function getBestUrduVoice(voices: SpeechSynthesisVoice[], preferredGender: string): SpeechSynthesisVoice | null {
  const urduVoices = voices.filter((v) => {
    const l = v.lang.toLowerCase();
    return l.startsWith('ur') || l.includes('ur-pk') || l.includes('ur_pk');
  });

  if (urduVoices.length === 0) return null;

  const scored = urduVoices.map((v) => {
    const name = v.name.toLowerCase();
    let score = 0;

    // Highest points for neural/natural indicators
    if (name.includes('natural')) score += 100;
    if (name.includes('neural')) score += 100;
    if (name.includes('siri')) score += 90;
    if (name.includes('premium')) score += 80;
    if (name.includes('online')) score += 70;
    
    // Windows native premium voices
    if (name.includes('uzma')) score += 60;
    if (name.includes('asad')) score += 60;
    
    // Google voices
    if (name.includes('google')) score += 50;

    // Gender matching points
    const isFemale = name.includes('female') || name.includes('uzma') || name.includes('zira') || name.includes('aria') || name.includes('sara') || name.includes('yasmin');
    const isMale = name.includes('male') || name.includes('asad') || name.includes('david') || name.includes('guy') || name.includes('naayf');

    if (preferredGender === 'female' && isFemale) score += 30;
    if (preferredGender === 'male' && isMale) score += 30;

    if (v.localService) score += 10;

    return { voice: v, score };
  });

  scored.sort((a, b) => b.score - a.score);
  return scored[0].voice;
}

/**
 * Scores and selects the most appropriate Arabic-trained neural voice if available.
 */
function getBestArabicVoice(voices: SpeechSynthesisVoice[], preferredGender: string): SpeechSynthesisVoice | null {
  const arabicVoices = voices.filter((v) => {
    const l = v.lang.toLowerCase();
    return l.startsWith('ar') || l.includes('ar-sa') || l.includes('ar_sa') || l.includes('ar-eg') || l.includes('ar_eg');
  });

  if (arabicVoices.length === 0) return null;

  const scored = arabicVoices.map((v) => {
    const name = v.name.toLowerCase();
    let score = 0;

    if (name.includes('natural')) score += 100;
    if (name.includes('neural')) score += 100;
    if (name.includes('siri')) score += 90;
    if (name.includes('premium')) score += 80;
    if (name.includes('online')) score += 70;
    
    // Windows/MS premium voices
    if (name.includes('hoda') || name.includes('naayf')) score += 60;
    
    if (name.includes('google')) score += 50;

    const isFemale = name.includes('female') || name.includes('hoda') || name.includes('zira') || name.includes('aria') || name.includes('sara') || name.includes('yasmin');
    const isMale = name.includes('male') || name.includes('naayf') || name.includes('david') || name.includes('guy');

    if (preferredGender === 'female' && isFemale) score += 30;
    if (preferredGender === 'male' && isMale) score += 30;

    if (v.localService) score += 10;

    return { voice: v, score };
  });

  scored.sort((a, b) => b.score - a.score);
  return scored[0].voice;
}

/**
 * Scores and selects the most appropriate English-trained neural voice if available.
 */
function getBestEnglishVoice(voices: SpeechSynthesisVoice[], preferredGender: string): SpeechSynthesisVoice | null {
  const englishVoices = voices.filter((v) => {
    const l = v.lang.toLowerCase();
    return l.startsWith('en') || l.includes('en-us') || l.includes('en-gb') || l.includes('en_us');
  });

  if (englishVoices.length === 0) return null;

  const scored = englishVoices.map((v) => {
    const name = v.name.toLowerCase();
    let score = 0;

    if (name.includes('natural')) score += 100;
    if (name.includes('neural')) score += 100;
    if (name.includes('siri')) score += 90;
    if (name.includes('premium')) score += 80;
    if (name.includes('online')) score += 70;
    if (name.includes('google')) score += 50;

    const isFemale = name.includes('female') || name.includes('zira') || name.includes('hazel') || name.includes('susan') || name.includes('heera') || name.includes('aria') || name.includes('premium female');
    const isMale = name.includes('male') || name.includes('david') || name.includes('mark') || name.includes('george') || name.includes('guy') || name.includes('ravi') || name.includes('premium male');

    if (preferredGender === 'female' && isFemale) score += 30;
    if (preferredGender === 'male' && isMale) score += 30;

    if (v.localService) score += 10;

    return { voice: v, score };
  });

  scored.sort((a, b) => b.score - a.score);
  return scored[0].voice;
}

/**
 * Speaks the given text using Web Speech API or High-Quality Google Neural/Translate TTS.
 * This implementation provides a natural, warm, child-friendly voice (preschool teacher tone)
 * with slow, clear pronunciation, custom volume, and proper language routing.
 */
export function speakText(
  text: string,
  lang: AppLanguage,
  soundEnabled: boolean = true,
  onStart?: () => void,
  onEnd?: () => void
) {
  if (!soundEnabled) {
    if (onStart) onStart();
    if (onEnd) onEnd();
    return;
  }

  // Safety format text to replace {childName} with active name if any exists in local storage
  let formattedText = text;
  if (formattedText.includes('{childName}')) {
    try {
      const activeProfileStr = localStorage.getItem('llw_active_profile');
      if (activeProfileStr) {
        const profile = JSON.parse(activeProfileStr);
        if (profile && profile.name) {
          formattedText = formattedText.replace(/{childName}/g, profile.name);
        }
      }
    } catch (e) {
      formattedText = formattedText.replace(/{childName}/g, 'Arham');
    }
  }

  // Get volume (0 to 1) from localStorage (default: 80%)
  let voiceVolume = 0.80;
  if (typeof window !== 'undefined') {
    const cachedVol = localStorage.getItem('llw_voice_volume');
    if (cachedVol !== null) {
      voiceVolume = parseFloat(cachedVol) / 100;
    }
  }

  // Get voice type from localStorage (default: 'male')
  let voiceType = 'male';
  if (typeof window !== 'undefined') {
    const cachedType = localStorage.getItem('llw_voice_type');
    if (cachedType !== null) {
      voiceType = cachedType;
    }
  }

  // Cancel and clean up any active HTML5 Audio playback
  if (activeAudio) {
    try {
      activeAudio.pause();
      activeAudio.onplay = null;
      activeAudio.onended = null;
      activeAudio.onerror = null;
    } catch (e) {
      console.warn('Error pausing previous audio:', e);
    }
    activeAudio = null;
  }

  // Cancel any active browser Web Speech synthesis
  if (typeof window !== 'undefined' && 'speechSynthesis' in window) {
    try {
      window.speechSynthesis.cancel();
    } catch (e) {
      console.warn('Error canceling speech synthesis:', e);
    }
  }

  // Unified Web Speech Synthesis runner (used as primary for English or as ultimate fallback)
  const runSpeechSynthesis = (txtToSpeak: string, targetLang: AppLanguage) => {
    if (typeof window === 'undefined' || !('speechSynthesis' in window)) {
      if (onStart) onStart();
      if (onEnd) onEnd();
      return;
    }

    const utterance = new SpeechSynthesisUtterance(txtToSpeak);
    utterance.volume = voiceVolume;

    // Set voice language code
    if (targetLang === 'en') {
      utterance.lang = 'en-US';
    } else if (targetLang === 'ur') {
      utterance.lang = 'ur-PK';
    } else if (targetLang === 'ar') {
      utterance.lang = 'ar-SA';
    }

    // Set up voice callbacks
    if (onStart) {
      utterance.onstart = onStart;
    }
    if (onEnd) {
      utterance.onend = onEnd;
      utterance.onerror = (e) => {
        console.warn('Speech utterance warning:', e);
        onEnd();
      };
    }

    const voices = window.speechSynthesis.getVoices();
    let selectedVoice: SpeechSynthesisVoice | null = null;

    if (targetLang === 'ur') {
      selectedVoice = getBestUrduVoice(voices, voiceType);
    } else if (targetLang === 'ar') {
      selectedVoice = getBestArabicVoice(voices, voiceType);
    } else {
      selectedVoice = getBestEnglishVoice(voices, voiceType);
    }

    if (selectedVoice) {
      utterance.voice = selectedVoice;
    }

    // Slow and clear pronunciation for children (preschool teacher style)
    utterance.rate = targetLang === 'ur' ? 0.75 : 0.80;
    
    // Adjust pitch based on selected voice type for a warm and child-friendly tone
    if (voiceType === 'male') {
      utterance.pitch = 0.95; // Warm, friendly male pitch
    } else {
      utterance.pitch = 1.15; // Soft, playful female pitch
    }

    try {
      window.speechSynthesis.speak(utterance);
    } catch (e) {
      console.warn('Speech play failed:', e);
      if (onEnd) onEnd();
    }
  };

  // Speaks using the high-quality Neural Google Translate TTS engine (via HTML5 Audio)
  const runGoogleTranslateTTS = (txtToSpeak: string, targetLang: AppLanguage, fallbackFn: () => void) => {
    const langCode = targetLang === 'ur' ? 'ur' : targetLang === 'ar' ? 'ar' : 'en';
    const primaryUrl = `https://translate.google.com/translate_tts?ie=UTF-8&tl=${langCode}&client=tw-ob&q=${encodeURIComponent(txtToSpeak)}`;
    const alternateUrl = `https://translate.googleapis.com/translate_tts?ie=UTF-8&tl=${targetLang === 'ur' ? 'ur-PK' : targetLang === 'ar' ? 'ar-SA' : 'en-US'}&client=gtx&q=${encodeURIComponent(txtToSpeak)}`;

    const playAudio = (url: string, onAudioError: () => void) => {
      const audio = document.createElement('audio');
      audio.setAttribute('referrerpolicy', 'no-referrer');
      try {
        (audio as any).referrerPolicy = 'no-referrer';
      } catch (e) {}

      audio.src = url;
      audio.volume = voiceVolume;
      
      // Slow down speed for a warm, clear child-friendly teacher tone (not robotic)
      audio.playbackRate = 0.80; 

      activeAudio = audio;

      if (onStart) {
        audio.onplay = () => {
          // Re-enforce slow, child-friendly playback speed when playback starts
          audio.playbackRate = 0.80;
          onStart();
        };
      } else {
        audio.onplay = () => {
          audio.playbackRate = 0.80;
        };
      }

      audio.onended = () => {
        if (activeAudio === audio) activeAudio = null;
        if (onEnd) onEnd();
      };

      audio.onerror = () => {
        if (activeAudio === audio) activeAudio = null;
        onAudioError();
      };

      audio.play().catch((err) => {
        console.warn('Google TTS audio play blocked or failed:', err);
        if (activeAudio === audio) activeAudio = null;
        onAudioError();
      });
    };

    // Try primary Google Translate TTS endpoint, fallback to alternate Google API, and finally run native Web Speech Synthesis
    playAudio(primaryUrl, () => {
      console.log('Primary Google TTS failed, trying alternate Google API endpoint...');
      playAudio(alternateUrl, () => {
        console.warn('Alternate Google TTS failed. Falling back to native browser speech synthesis...');
        fallbackFn();
      });
    });
  };

  // Language specific routing:
  // - Urdu & Arabic: If an Urdu/Arabic-trained high-quality neural voice is found in the browser, use Web Speech Synthesis.
  //   Otherwise, fallback to Google Translate high-quality child-friendly voice engine.
  if (lang === 'ur' || lang === 'ar') {
    const voices = typeof window !== 'undefined' && 'speechSynthesis' in window ? window.speechSynthesis.getVoices() : [];
    const hasHighQualityVoice = lang === 'ur' 
      ? getBestUrduVoice(voices, voiceType) !== null
      : getBestArabicVoice(voices, voiceType) !== null;

    if (hasHighQualityVoice) {
      // Use the high-quality browser voice directly!
      runSpeechSynthesis(formattedText, lang);
    } else {
      // Fallback to Google Translate Neural TTS
      runGoogleTranslateTTS(formattedText, lang, () => {
        // Last-resort fallback to Web Speech Synthesis
        runSpeechSynthesis(formattedText, lang);
      });
    }
  } else {
    // English language routing
    if (typeof window !== 'undefined' && 'speechSynthesis' in window) {
      const voices = window.speechSynthesis.getVoices();
      const bestEngVoice = getBestEnglishVoice(voices, voiceType);

      if (bestEngVoice) {
        // We have a high-quality neural English voice available in browser, use it!
        runSpeechSynthesis(formattedText, 'en');
      } else {
        // Fallback to high-quality Google Translate English TTS
        runGoogleTranslateTTS(formattedText, 'en', () => {
          runSpeechSynthesis(formattedText, 'en');
        });
      }
    } else {
      // No speechSynthesis support, try Google Translate TTS
      runGoogleTranslateTTS(formattedText, 'en', () => {
        if (onStart) onStart();
        if (onEnd) onEnd();
      });
    }
  }
}

/**
 * Triggers a short playful custom sound synthesizer using the Web Audio API
 * (like bubble pops, level-ups, error bells, star collection!)
 */
export function playSynthSound(type: 'pop' | 'star' | 'badge' | 'error' | 'click') {
  if (typeof window === 'undefined' || !('AudioContext' in window || 'webkitAudioContext' in window)) {
    return;
  }

  try {
    const AudioContextClass = window.AudioContext || (window as any).webkitAudioContext;
    const ctx = new AudioContextClass();
    
    if (type === 'pop') {
      // Balloon pop: quick pitch drop with a short sine wave
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.connect(gain);
      gain.connect(ctx.destination);

      osc.type = 'sine';
      osc.frequency.setValueAtTime(400, ctx.currentTime);
      osc.frequency.exponentialRampToValueAtTime(80, ctx.currentTime + 0.12);

      gain.gain.setValueAtTime(0.3, ctx.currentTime);
      gain.gain.linearRampToValueAtTime(0, ctx.currentTime + 0.12);

      osc.start();
      osc.stop(ctx.currentTime + 0.13);
    } else if (type === 'star') {
      // Star collected: sparkling dual arpeggio
      const playTone = (freq: number, start: number, duration: number) => {
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.connect(gain);
        gain.connect(ctx.destination);

        osc.type = 'triangle';
        osc.frequency.setValueAtTime(freq, ctx.currentTime + start);

        gain.gain.setValueAtTime(0.2, ctx.currentTime + start);
        gain.gain.linearRampToValueAtTime(0, ctx.currentTime + start + duration);

        osc.start(ctx.currentTime + start);
        osc.stop(ctx.currentTime + start + duration);
      };

      playTone(523.25, 0, 0.15); // C5
      playTone(659.25, 0.1, 0.15); // E5
      playTone(783.99, 0.2, 0.3); // G5
    } else if (type === 'badge') {
      // Level up / badge unlocked! Major chord sweep
      const freqs = [523.25, 659.25, 783.99, 1046.50]; // C5, E5, G5, C6
      freqs.forEach((freq, idx) => {
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.connect(gain);
        gain.connect(ctx.destination);
        osc.type = 'sine';
        osc.frequency.setValueAtTime(freq, ctx.currentTime + idx * 0.08);
        gain.gain.setValueAtTime(0.25, ctx.currentTime + idx * 0.08);
        gain.gain.linearRampToValueAtTime(0, ctx.currentTime + idx * 0.08 + 0.35);
        osc.start(ctx.currentTime + idx * 0.08);
        osc.stop(ctx.currentTime + idx * 0.08 + 0.36);
      });
    } else if (type === 'error') {
      // Flat buzzer sound
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.connect(gain);
      gain.connect(ctx.destination);

      osc.type = 'sawtooth';
      osc.frequency.setValueAtTime(130, ctx.currentTime);
      osc.frequency.linearRampToValueAtTime(100, ctx.currentTime + 0.25);

      gain.gain.setValueAtTime(0.2, ctx.currentTime);
      gain.gain.linearRampToValueAtTime(0, ctx.currentTime + 0.25);

      osc.start();
      osc.stop(ctx.currentTime + 0.26);
    } else if (type === 'click') {
      // Light click pop
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.connect(gain);
      gain.connect(ctx.destination);

      osc.type = 'sine';
      osc.frequency.setValueAtTime(600, ctx.currentTime);
      gain.gain.setValueAtTime(0.15, ctx.currentTime);
      gain.gain.linearRampToValueAtTime(0, ctx.currentTime + 0.05);

      osc.start();
      osc.stop(ctx.currentTime + 0.06);
    }
  } catch (e) {
    console.error('AudioContext synth error:', e);
  }
}
